const SYNTHETIC_EMAIL_SUFFIX = "@meta-lead.local";

/**
 * Returns true when the email is a Meta lead synthetic placeholder.
 *
 * @param email - The email address to test.
 * @returns True when the email uses the synthetic Meta-lead suffix.
 */
export function isSyntheticContact(email: string): boolean {
  return Boolean(email) && email.endsWith(SYNTHETIC_EMAIL_SUFFIX);
}

/**
 * Parses platform and external user id from a synthetic Meta lead email.
 *
 * @param email - The synthetic Meta-lead email to parse.
 * @returns The platform and external user id, or null when not a synthetic lead.
 */
export function extractSocialFromSyntheticEmail(email: string): { platform: string; externalUserId: string } | null {
  if (!isSyntheticContact(email)) return null;
  const match = email.match(/^(fb|ig)_(.+)@meta-lead\.local$/);
  if (!match) return null;
  const platform = match[1] === "ig" ? "instagram" : "facebook";
  return { platform, externalUserId: match[2] };
}

/**
 * Builds a public profile URL for a social platform and external user id.
 *
 * Instagram's external user id is a messaging-scoped IGSID that Meta never
 * resolves into a public profile page (`instagram.com/{igsid}` 404s) --
 * pass the account's real `handle` (username) when known to build a URL
 * that actually works. Facebook's numeric id does resolve via profile.php,
 * so it has no equivalent handle parameter.
 *
 * @param platform - The social platform (`instagram` or `facebook`).
 * @param externalUserId - The platform-specific user identifier.
 * @param handle - The real Instagram username, when known.
 * @returns The public profile URL for the given platform and user.
 */
export function getSocialProfileUrl(platform: string, externalUserId: string, handle?: string): string {
  if (platform === "instagram") {
    return `https://www.instagram.com/${handle || externalUserId}`;
  }
  return `https://www.facebook.com/profile.php?id=${externalUserId}`;
}

/**
 * Whether a contact's capture source is a page-scoped messaging id (a
 * Messenger PSID or Instagram IGSID from a DM/button click) rather than a
 * public post interaction -- those ids never resolve via a public profile
 * URL, so they need the conversation link instead.
 *
 * @param source - The contact's capture source (message, postback, comment, ...).
 * @returns True for a message/postback-sourced contact.
 */
export function isMessageSourced(source?: string): boolean {
  return source === "message" || source === "postback";
}

/**
 * Builds a link that opens the DM conversation with this contact, instead
 * of a public profile that a page-scoped id can never resolve to.
 *
 * Facebook Messenger's `/messages/t/{psid}` opens that thread directly for
 * anyone with a role on the connected Page. Instagram has no id-based
 * equivalent -- `ig.me/m/{username}` (Meta's own "message me" link format)
 * needs the real username, so it's only available once `handle` is known
 * (see the DM-sender profile lookup in the Instagram webhook route).
 *
 * @param platform - The social platform (`instagram` or `facebook`).
 * @param externalUserId - The platform-specific user identifier.
 * @param handle - The real Instagram username, when known.
 * @returns The conversation URL, or null when it can't be built.
 */
export function getSocialConversationUrl(platform: string, externalUserId: string, handle?: string): string | null {
  if (platform === "facebook") {
    return `https://www.facebook.com/messages/t/${externalUserId}`;
  }
  if (platform === "instagram") {
    return handle ? `https://ig.me/m/${handle}` : null;
  }
  return null;
}

/**
 * Returns a display label for a social platform.
 *
 * @param platform - The social platform key.
 * @returns The human-readable platform label.
 */
export function getSocialProfileLabel(platform: string): string {
  if (platform === "instagram") return "Instagram";
  return "Facebook";
}

const CONTACT_LABELS: Record<string, string> = {
  facebook: "Facebook",
  instagram: "Instagram",
  whatsapp: "WhatsApp",
  linkedin: "LinkedIn",
  phone: "Phone",
  email: "Email",
};

/**
 * Returns a display label for a contact channel type.
 *
 * @param type - The contact channel type key.
 * @returns The label for known types, or a capitalized fallback.
 */
export function getContactLabel(type: string): string {
  if (CONTACT_LABELS[type]) return CONTACT_LABELS[type];
  return type.charAt(0).toUpperCase() + type.slice(1);
}

const CONTACT_HREF_BUILDERS: Record<string, (value: string, handle?: string, source?: string) => string | null> = {
  // A message/postback id is page-scoped and never resolves via profile.php
  // -- link to the DM thread instead, which always works for any id.
  facebook: (value, _handle, source) =>
    isMessageSourced(source) ? getSocialConversationUrl("facebook", value) : getSocialProfileUrl("facebook", value),
  // No handle means only the dead IGSID-based URL is available -- omit the
  // link entirely rather than send the user to a guaranteed 404.
  instagram: (value, handle, source) =>
    isMessageSourced(source)
      ? getSocialConversationUrl("instagram", value, handle)
      : (handle ? getSocialProfileUrl("instagram", value, handle) : null),
  whatsapp: (value) => `https://wa.me/${value.replace(/\D/g, "")}`,
  linkedin: (value) => `https://www.linkedin.com/in/${value}`,
  email: (value) => `mailto:${value}`,
};

/**
 * Returns a clickable href for a contact channel, or null when unsupported
 * or when the link is known to be dead (e.g. an Instagram contact with no
 * resolved handle).
 *
 * @param type - The contact channel type key.
 * @param value - The raw contact value (id, phone, email, or handle).
 * @param handle - The real username, when known (Instagram only).
 * @param source - The contact's capture source (facebook/instagram only).
 * @returns A navigable href for supported types, or null otherwise.
 */
export function getContactHref(type: string, value: string, handle?: string, source?: string): string | null {
  const builder = CONTACT_HREF_BUILDERS[type];
  return builder ? builder(value, handle, source) : null;
}

/**
 * Whether a social contact's link opens a DM conversation rather than a
 * public profile -- callers use this to pick the right label ("Abrir
 * conversa" vs "Ver perfil").
 *
 * @param type - The contact channel type key.
 * @param source - The contact's capture source.
 * @returns True when the contact's href (if any) is a conversation link.
 */
export function isConversationLink(type: string, source?: string): boolean {
  return (type === "facebook" || type === "instagram") && isMessageSourced(source);
}

/**
 * Returns true when the contact type is a social platform whose raw value
 * (an opaque user ID) should not be displayed directly.
 * Callers should substitute a translated label (e.g. "Ver perfil") instead.
 *
 * @param type - The contact channel type key.
 * @returns True when the value should be hidden behind a label.
 */
export function isSocialContact(type: string): boolean {
  return type === "facebook" || type === "instagram" || type === "linkedin";
}
